import React, { Suspense, useEffect, useRef } from 'react';
import { Stack, Tooltip } from '@mui/material';
import { Button } from 'new/elements/Button';
import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import Drawer from 'new/components/drawer/Drawer';
import { useHistory, useLocation } from 'react-router-dom';
import { Add, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import RouteLoadingComponent from 'js/components/route_utils/RouteLoadingComponent';
import styles from './Integrate.module.scss';
import structure from 'new/routes/structure';
import { FlowSelectionPage } from './FlowSelectionPage';
import { FlowSelectPopover } from './FlowSelectPopover/FlowSelectPopover';
import { useRestrictedTiers } from 'new/hooks/useRestrictedTiers';

const basePath = `${ROUTE_REDESIGN}${ROUTE_CONNECT}`;
export const SELECT_FLOW_TYPES_URI = `${basePath}/flows/select-type`;

const sidebarGroups = structure.find((s) => s.name === 'Integrate').groups();

export function DrawerWithNewDataFlowButton({ children }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const history = useHistory();
  const { hasExpressTierLimitedAccess } = useRestrictedTiers();
  const params = new URLSearchParams(history.location.search);
  // for disabling button if path === SELECT_FLOW_TYPES_URI
  const pathname = useLocation().pathname;
  const isDisabled = pathname === SELECT_FLOW_TYPES_URI;

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  useEffect(() => {
    const action = params.get('action');
    if (action && action === 'create' && buttonRef.current) {
      buttonRef.current.click();
    }
  }, []);

  const renderHeaderSlot = (expanded) => {
    if (expanded) {
      return (
        <Stack>
          <Button
            ref={buttonRef}
            className={styles.newFlowButtonPopover}
            variant="contained"
            color={isDisabled ? 'info' : 'primary'}
            data-testid="new-data-flow-button"
            size="medium"
            endIcon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            aria-describedby={id}
            fullWidth
            onClick={handleClick}
            disabled={isDisabled}
          >
            Create New Flow
          </Button>
        </Stack>
      );
    }
    return (
      <Tooltip title="New Data Flow" placement="right">
        <Button
          ref={buttonRef}
          className={styles.newFlowButton}
          variant="ghost"
          color={isDisabled ? 'info' : 'primary'}
          data-testid="new-data-flow-button"
          disabled={isDisabled}
          onClick={handleClick}
          aria-describedby={id}
          aria-label="New Data Flow"
          fullWidth={false}
        >
          <Add />
        </Button>
      </Tooltip>
    );
  };

  return (
    <Drawer
      content={sidebarGroups as any}
      headerSlot={hasExpressTierLimitedAccess ? null : renderHeaderSlot}
    >
      <FlowSelectPopover id={id} open={open} anchorEl={anchorEl} onClose={handleClose}>
        <FlowSelectionPage onSelection={handleClose} />
      </FlowSelectPopover>
      <Suspense fallback={<RouteLoadingComponent />}>{children}</Suspense>
    </Drawer>
  );
}
